import { useBookingSelectionsStore } from '@/stores/bookingSelections'
import { useCartStore } from '@/stores/cart'
import { useContentStore } from '@/stores/content'
import { useMainStore } from '@/stores/main'
import { defineStore } from 'pinia'

import dayjs from 'dayjs'

import { CadyAddOn } from '@/lib/cady/cadyEnums'
import { getBundleAddOnIds } from '@/lib/cartItemAttributes'
import { buildSessionSectionFeatures, type CartSections } from '@/lib/cartSections'
import { mergeCmsOverFallback } from '@/lib/contentHelpers'
import { ContentType, sessionDealBatchItems } from '@/lib/contentKeys'
import { formatLocationFullAddress } from '@/lib/locationAddress'
import { LocationTypeId } from '@/lib/scheduling/locationTypeId'
import {
  buildSceneSelectionGroups,
  deriveAddOnsImagePosition,
  deriveAddOnsTheme,
  getPreSelectedAddOnIds,
  isRoadshowCompatibleScene,
  localSceneId,
  resolveCartBundleScenes
} from '@/lib/sessionCatalog'
import { formatCurrencyPrecise, formatPriceDelta } from '@/lib/utils'
import Sunny16CommerceBookingCatalogService from '@/services/Sunny16CommerceBookingCatalogService'
import Sunny16CommerceCadyService from '@/services/Sunny16CommerceCadyService'

import {
  type AddOnSectionEnriched,
  AddOnSectionKind,
  AddOnSelectionState,
  type AddOnsPageContent,
  ELITE_TRIGGER_ID_TO_CADY_ADDON
} from '@/components/AddOnsSelection/types'
import type { PrepayCardEnriched } from '@/components/CheckoutPrePay/types'
import type { CartUpsellEnriched } from '@/components/CheckoutSummary/types'
import { SchedulingLocationToggleType } from '@/components/Scheduling/types'

import type {
  AddOnBundleWithContent,
  BookingSessionTypeDtoWithContent,
  PrepayBundleWithContent
} from '@/types/catalogWithContent'
import type { SessionBundleContent } from '@/types/sessionBundleEnriched'

import { CadyScene } from '@VelocitySquared/cady-sunny16-commerce-integration-models'
import type {
  CatalogBundleStorefrontDto,
  ItemStorefrontDto
} from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'
import type {
  BookingCatalogDto,
  BookingSchoolDto,
  BookingSessionTypeDto,
  BookingSessionTypesListDto
} from '@VelocitySquared/sunny16-commerce-dataobjects/storefront/booking'
import type {
  AddOnsSelectionPage as CmsAddOnsSelectionPage,
  BookingAddOnContent,
  BookingSessionBundle,
  CheckoutCartUpsell,
  CheckoutPremierRetouching,
  CheckoutPrepayCard,
  SceneCatalogEntry,
  SceneSelectionPage as CmsSceneSelectionPage
} from '@VelocitySquared/sunny16-content-dataobjects-cady/booking'

import { addOnsPageFallbacks } from '@/content/add-ons-selection/addOnsPageContentRegistry'
import { addOnContentFallbacks } from '@/content/add-ons-selection/addOnsSelection'
import {
  checkoutPremierRetouchingFallback,
  prepayCardFallbacks
} from '@/content/checkout-prepay/prepayContent'
import { cartUpsellFallbacks } from '@/content/checkout-summary/cartUpsells'
import { sceneCatalogFallback } from '@/content/scene-selection/sceneSelection'
import { sceneSelectionPageFallbacks } from '@/content/scene-selection/sceneSelectionPageContentRegistry'
import { sessionBundleFallbacks } from '@/content/sessionCatalogBundles'

import { SessionType, type SessionTypeKey } from './sessionType'
import { storeId } from './storeId'

export { SessionType, type SessionTypeKey }

interface BookingCommerceStoreState {
  /** Full school detail from the booking school endpoint. */
  selectedSchoolDetail: BookingSchoolDto | null
  /** Full catalog response from the API. */
  catalogData: BookingCatalogDto | null
  /** Whether a catalog request is in flight. */
  catalogLoading: boolean
  /** Error message from the most recent catalog request, if any. */
  catalogError: string | null
}

/**
 * Map a pre-pay bundle to its content-registry key based on the `CADY_AddOnId`
 * attributes of its items, not the (mutable) bundle identifier string.
 * Returns null for the Premier Retouching pre-pay — that bundle drives the
 * retouching drawer, not a card on the pre-pay page. When All Digitals is the
 * session type's only real prepay option (no Portfolio counterpart), it gets
 * the dedicated single-offer entry so Marketing can author that copy (e.g.
 * the `BasicPrepayItem` promo badge) independently of the standard card —
 * this is driven by the catalog's actual prepay count for the session type,
 * not a hardcoded list of session types.
 */
function derivePrepayCardKey(
  prepay: CatalogBundleStorefrontDto,
  realPrepayCount: number
): string | null {
  const addOnIds = getBundleAddOnIds(prepay)
  if (addOnIds.has(CadyAddOn.PremierRetouching)) return null
  if (addOnIds.has(CadyAddOn.Portfolio)) return 'PREPAY_DIGITAL_PORTFOLIO'
  if (addOnIds.has(CadyAddOn.AllDigitals)) {
    return realPrepayCount === 1 ? 'PREPAY_BASIC_ALLDIGITALS' : 'PREPAY_ALLDIGITALS'
  }
  return null
}

export const useBookingCommerceStore = defineStore(storeId.BOOKING_COMMERCE, {
  state: (): BookingCommerceStoreState => ({
    selectedSchoolDetail: null,
    catalogData: null,
    catalogLoading: false,
    catalogError: null
  }),
  getters: {
    /** School data — sourced from the parallel `getSchoolDetails()` call, not the catalog response. */
    schoolData(): BookingSchoolDto | null {
      return this.selectedSchoolDetail ?? null
    },
    /** True when the selected school permits the customer to opt out of receiving the proof portfolio. Sourced from `BookingSchoolDto.isPortfolioOptional`. */
    schoolAllowsPortfolioOptOut(): boolean {
      return this.selectedSchoolDetail?.isPortfolioOptional ?? false
    },
    /**
     * Whether the selected school permits black & white images for the 2nd
     * casual yearbook pose. Sourced from
     * `BookingSchoolDto.isBlackAndWhiteSceneAllowed`, which the API derives
     * from the school's Summit pose-2 configuration: a pose 2 typed "Casual"
     * without the companion "Casual Allow Black & White" pose type means B&W
     * is not permitted, and Scene Selection hides Shadowland + Exposure
     * accordingly (see `shouldHideBlackAndWhiteScenes`).
     *
     * Defaults to `true` when school detail hasn't loaded — matching the API
     * rule that a school with no pose 2 configured offers every scene.
     */
    schoolAllowsBlackAndWhiteScenes(): boolean {
      return this.schoolData?.isBlackAndWhiteSceneAllowed ?? true
    },
    /**
     * Whether the selected school participates in the Roadshow Plus flow.
     *
     * Sourced from the school detail DTO (`BookingSchoolDto.isRoadshowPlus`)
     * inside the catalog response — NOT from
     * `mainStore.selectedSchool.isRoadshowPlus` (the school-list Simple DTO),
     * which the backend currently stubs to `false` for every school.
     *
     * Returns `false` when the catalog hasn't loaded yet. Every RS+ flow
     * gate (Session Listing, Scene Selection, Schedule, Checkout, Landing
     * page recommendation suppression) reads from here.
     */
    isRoadshowPlus(): boolean {
      return this.schoolData?.isRoadshowPlus ?? false
    },
    /**
     * Whether the RS+-exclusive Outdoor "fake add-on" should be offered
     * on the Add-Ons page. True for RS+-at-school users on the traditional
     * tiers (Simple / Standard / Legacy RoadshowPlus). The view renders
     * the card from this flag; the guard uses it to keep the page in the
     * flow even when the tier itself has no real add-ons.
     */
    keyHasRsPlusOutdoorAddOnOffer(): (key: SessionTypeKey | null | undefined) => boolean {
      return (key) => {
        if (!key) return false
        if (!this.isRoadshowPlus) return false
        const selections = useBookingSelectionsStore()
        if (selections.selectedLocationType !== SchedulingLocationToggleType.School) return false
        return (
          key === SessionType.SimpleRoadshowPlus ||
          key === SessionType.StandardRoadshowPlus ||
          key === SessionType.LegacyRoadshowPlus
        )
      }
    },
    hasRsPlusOutdoorAddOnOffer(): boolean {
      const selections = useBookingSelectionsStore()
      return this.keyHasRsPlusOutdoorAddOnOffer(selections.selectedSessionTypeKey)
    },
    /** The full sessionTypes object from the catalog. */
    sessionTypes(): BookingSessionTypesListDto | null {
      return this.catalogData?.sessionTypes ?? null
    },
    /**
     * Session types that are present (non-null) in the catalog, returned as
     * an array of [key, dto] tuples ordered by the DTO's natural key order.
     */
    availableSessionTypes(): [SessionTypeKey, BookingSessionTypeDto][] {
      if (!this.sessionTypes) return []
      const entries = Object.entries(this.sessionTypes) as [
        SessionTypeKey,
        BookingSessionTypeDto | null
      ][]
      return entries.filter(
        (entry): entry is [SessionTypeKey, BookingSessionTypeDto] => entry[1] !== null
      )
    },
    /** Number of session types available in the catalog. */
    availableSessionTypeCount(): number {
      return this.availableSessionTypes.length
    },
    /** True when catalog data has been loaded successfully. */
    isDataReady(): boolean {
      return this.catalogData !== null
    },
    /** True if the most recent load attempt failed. */
    hasLoadError(): boolean {
      return this.catalogError !== null
    },
    /**
     * Look up a content bundle for a session type by key.
     * Extracts `primarySessionBundle.content` from `catalogWithContent`.
     *
     * @param key - Session type key.
     * @returns Content bundle, or undefined if the key is not in the catalog.
     */
    getMappedBundle(): (key: SessionTypeKey | undefined) => SessionBundleContent | undefined {
      return (key) => (key ? this.catalogWithContent[key]?.primarySessionBundle.content : undefined)
    },
    /**
     * Look up the base price for a session type by key.
     * Sourced from `pricing.basePrice` on the primary bundle.
     *
     * @param key - Session type key.
     * @returns Base price, or 0 if the key is not in the catalog or key is undefined.
     */
    getBasePrice(): (key: SessionTypeKey | undefined) => number {
      return (key) =>
        key ? (this.catalogWithContent[key]?.primarySessionBundle.pricing?.basePrice ?? 0) : 0
    },
    /** Flat array of all content bundles in tier order. */
    allMappedBundles(): SessionBundleContent[] {
      return Object.values(this.catalogWithContent).map((st) => st!.primarySessionBundle.content)
    },
    /** Content bundles keyed by SessionTypeKey for direct lookup. */
    mappedBundlesByKey(): Partial<Record<SessionTypeKey, SessionBundleContent>> {
      const result: Partial<Record<SessionTypeKey, SessionBundleContent>> = {}
      for (const [key, st] of Object.entries(this.catalogWithContent)) {
        if (st) result[key as SessionTypeKey] = st.primarySessionBundle.content
      }
      return result
    },
    /** Base prices keyed by SessionTypeKey for direct lookup. */
    basePriceByKey(): Partial<Record<SessionTypeKey, number>> {
      const result: Partial<Record<SessionTypeKey, number>> = {}
      for (const [key, st] of Object.entries(this.catalogWithContent)) {
        if (st) result[key as SessionTypeKey] = st.primarySessionBundle.pricing?.basePrice ?? 0
      }
      return result
    },
    /**
     * Return content bundles for a list of keys, filtering out any keys
     * that are not in the catalog or have no content registered.
     *
     * @param keys - Session type keys in the desired display order.
     * @returns Content bundles in the same order, with missing entries removed.
     */
    bundlesForKeys(): (keys: SessionTypeKey[]) => SessionBundleContent[] {
      return (keys) =>
        keys
          .map((k) => this.catalogWithContent[k]?.primarySessionBundle.content)
          .filter((b): b is SessionBundleContent => b !== undefined)
    },
    /**
     * The central enrichment pipeline. Merges API session type DTOs with
     * content registries to produce display-ready objects for each tier.
     *
     * For each session type key:
     * 1. Looks up the API DTO and content bundle — skips if either is missing
     * 2. Enriches add-ons via `addOnContentRegistry` (identifier → display content)
     * 3. Enriches pre-pays via `prepayContentRegistry` (identifier → display content)
     * 4. Builds scene selection content from API item groups + page content registry
     * 5. Stamps `sessionTypeKey` onto the content bundle for downstream reference
     *
     * @returns Enriched session types keyed by SessionTypeKey. Only includes
     *          tiers that have both API data AND a registered content bundle.
     */
    catalogWithContent(): Partial<Record<SessionTypeKey, BookingSessionTypeDtoWithContent>> {
      if (!this.sessionTypes) return {}
      const contentStore = useContentStore()
      const result: Partial<Record<SessionTypeKey, BookingSessionTypeDtoWithContent>> = {}
      for (const key of Object.keys(this.sessionTypes) as SessionTypeKey[]) {
        const dto = this.sessionTypes[key]
        if (!dto) continue
        const bundleGuid = dto.primarySessionBundle.bundleId
        const cmsFallback = sessionBundleFallbacks[key]
        if (!cmsFallback) continue
        // Resolve CMS content with fallback — unwraps all ContentRef wrappers
        const resolved = contentStore.resolve<BookingSessionBundle>(
          'booking-session-bundle',
          bundleGuid,
          cmsFallback
        )
        const addOns: AddOnBundleWithContent[] = dto.addOns.map((addon) => {
          const id = addon.bundle?.identifier ?? ''
          const fallback = addOnContentFallbacks[id] ?? null
          return {
            ...addon,
            sessionTypeKey: key,
            content: fallback
              ? contentStore.resolve<BookingAddOnContent>(ContentType.ADD_ON_CONTENT, id, fallback)
              : null
          }
        })
        const realPrepayCount = dto.prePays.filter(
          (p) => !getBundleAddOnIds(p).has(CadyAddOn.PremierRetouching)
        ).length
        const prePays: PrepayBundleWithContent[] = dto.prePays.map((prepay) => {
          const cardKey = derivePrepayCardKey(prepay, realPrepayCount)
          const fallback = cardKey ? (prepayCardFallbacks[cardKey] ?? null) : null
          return {
            ...prepay,
            sessionTypeKey: key,
            content:
              fallback && cardKey
                ? contentStore.resolve<CheckoutPrepayCard>(
                    ContentType.CHECKOUT_PREPAY_CARD,
                    cardKey,
                    fallback
                  )
                : null
          }
        })
        // Wire scene selection content from CMS ref or flat fallback
        const resolvedScenePage = resolved.sceneSelectionPage as unknown as
          | CmsSceneSelectionPage
          | undefined
        const scenePageContent = resolvedScenePage ?? sceneSelectionPageFallbacks[key]
        const itemGroups = dto.primarySessionBundle.bundle?.itemGroups ?? []
        const availableSceneEnumIds = this.schoolData?.availableStudioSceneIds
        const sceneGroups =
          scenePageContent && itemGroups.length > 0
            ? buildSceneSelectionGroups(itemGroups, scenePageContent, key, availableSceneEnumIds)
            : undefined
        result[key] = {
          sessionTypeKey: key,
          primarySessionBundle: {
            ...dto.primarySessionBundle,
            sessionTypeKey: key,
            content: { ...resolved, sessionTypeKey: key },
            sceneGroups
          },
          addOns,
          prePays,
          cartUpsells: dto.cartUpsells ?? []
        }
      }
      return result
    },
    /**
     * Look up a content-enriched session type by key.
     * Returns undefined if the key is not in the catalog or has no content.
     */
    sessionTypeWithContent(): (
      key: SessionTypeKey
    ) => BookingSessionTypeDtoWithContent | undefined {
      return (key) => this.catalogWithContent[key]
    },
    /**
     * Look up content for a session type by key.
     * Shorthand for `sessionTypeWithContent(key)?.primarySessionBundle.content`.
     */
    sessionTypeContent(): (key: SessionTypeKey) => SessionBundleContent | undefined {
      return (key) => this.catalogWithContent[key]?.primarySessionBundle.content
    },
    /**
     * Collect all catalog bundle IDs valid for a session type (add-ons,
     * prepays, and cart upsells). Used by upgrade cart operations to
     * determine which cart bundles to keep vs. remove.
     */
    getValidCatalogBundleIds(): (key: SessionTypeKey) => string[] {
      return (key) => {
        const dto = this.catalogWithContent[key]
        if (!dto) return []
        const addOnIds = dto.addOns.map((a) => a.catalogBundleId).filter(Boolean)
        const prepayIds = dto.prePays.map((p) => p.catalogBundleId).filter(Boolean)
        const upsellIds = dto.cartUpsells
          .map((u) => u.catalogBundle?.catalogBundleId)
          .filter((id): id is string => !!id)
        return [...addOnIds, ...prepayIds, ...upsellIds]
      }
    },
    /** Flat array of all content-enriched session types in tier order. */
    allCatalogWithContent(): BookingSessionTypeDtoWithContent[] {
      return Object.values(this.catalogWithContent)
    },
    /** Number of session types that have both API data and content. */
    catalogWithContentCount(): number {
      return this.allCatalogWithContent.length
    },
    /**
     * Assemble a complete AddOnsPageContent for a session type by merging
     * the API add-ons array with page-level display content from the
     * content registry.
     *
     * @param key - Session type to build add-on content for.
     * @returns Assembled page content, or null if the tier has no add-ons
     *          or no page display content registered.
     */
    getAddOnsPageContent(): (key: SessionTypeKey) => AddOnsPageContent | null {
      return (key) => {
        const sessionType = this.catalogWithContent[key]
        if (!sessionType) return null
        // Cast through `unknown` to drop the `DeepResolved` ContentRef-wrapper distinction — only flat fields are read.
        const resolvedAddOnsPage = sessionType.primarySessionBundle.content
          .addOnsSelectionPage as unknown as CmsAddOnsSelectionPage | undefined
        const fallbackContent = addOnsPageFallbacks[key]
        if (!resolvedAddOnsPage && !fallbackContent) return null
        // The catalog-content path doesn't route through `contentStore.resolve()`,
        // so merge here directly — same semantics as the resolver's per-level overlay.
        const pageContent = mergeCmsOverFallback<CmsAddOnsSelectionPage>(
          resolvedAddOnsPage,
          fallbackContent
        )
        const theme = deriveAddOnsTheme(key)
        const firstSectionImagePosition = deriveAddOnsImagePosition(key)
        // Per ADR 011, Elite-tier hair/makeup are upgrade triggers rendered
        // client-side, not catalog-driven add-ons. Filter any add-on
        // bundles whose identifiers appear in `ELITE_TRIGGER_ID_TO_CADY_ADDON`
        // (HAIR, MAKEUP) — synthesized sections replace them below.
        const isElite = key === SessionType.Elite
        const filteredAddOns = isElite
          ? sessionType.addOns.filter(
              (a) =>
                !(a.bundle?.identifier && a.bundle.identifier in ELITE_TRIGGER_ID_TO_CADY_ADDON)
            )
          : sessionType.addOns
        const sections: AddOnSectionEnriched[] = filteredAddOns
          .filter(
            (
              addon
            ): addon is AddOnBundleWithContent & {
              content: NonNullable<AddOnBundleWithContent['content']>
            } => addon.content !== null
          )
          .map((addon) => {
            const priceValue = addon.pricing?.basePrice ?? 0
            return {
              ...addon.content,
              addButtonPriceValue: priceValue,
              addButtonPrice: formatPriceDelta(priceValue),
              kind: AddOnSectionKind.ADDON
            }
          })
        if (isElite) {
          sections.push(...this.synthesizeEliteHairMakeupTriggers)
        }
        if (sections.length === 0 && !this.keyHasRsPlusOutdoorAddOnOffer(key)) return null
        return {
          heading: pageContent.heading,
          subtitle: pageContent.subtitle,
          backButtonLabel: pageContent.backButtonLabel,
          ctaLabel: pageContent.ctaLabel ?? 'Continue to Schedule',
          theme,
          firstSectionImagePosition,
          sections,
          preSelectedSectionIds: getPreSelectedAddOnIds(key, sections),
          validationErrorMessage: pageContent.validationErrorMessage || undefined,
          hairAndMakeupUpgradeLabel: pageContent.hairAndMakeupUpgradeLabel || undefined,
          hairAndMakeupUpgradeLabelMobile: pageContent.hairAndMakeupUpgradeLabelMobile || undefined,
          hairMakeupIncludedLabel: pageContent.hairMakeupIncludedLabel,
          upgradeSuccessToastTemplate: pageContent.upgradeSuccessToastTemplate || undefined
        }
      }
    },
    /**
     * Assemble PrepayCardContent[] for a session type by merging API pre-pay
     * bundles with content registry display content. Pricing comes from the
     * API; display content (images, descriptions, promo badge) from the registry.
     *
     * @param key - Session type to build pre-pay cards for.
     * @returns Cards for pre-pays with a matching content entry. Empty array
     *          if the tier has no pre-pays or none have registered content.
     */
    getPrepayCards(): (key: SessionTypeKey) => PrepayCardEnriched[] {
      return (key) => {
        const sessionType = this.catalogWithContent[key]
        if (!sessionType) return []
        return sessionType.prePays
          .filter(
            (
              prepay
            ): prepay is PrepayBundleWithContent & {
              content: NonNullable<PrepayBundleWithContent['content']>
            } => prepay.content !== null
          )
          .map((prepay) => ({
            ...prepay.content,
            salePrice: prepay.pricing?.basePrice ?? 0,
            retailPrice: prepay.compareAtPrice ?? undefined,
            catalogBundleId: prepay.catalogBundleId
          }))
      }
    },
    /**
     * Find the Premier Retouching bundle from the API pre-pays array for a
     * given session type. Returns the enriched DTO if found, or null.
     * Used to source retouching pricing and catalogBundleId from the API.
     */
    getPremierRetouchingBundle(): (key: SessionTypeKey) => PrepayBundleWithContent | null {
      return (key) => {
        const sessionType = this.catalogWithContent[key]
        if (!sessionType) return null
        return (
          sessionType.prePays.find((p) => getBundleAddOnIds(p).has(CadyAddOn.PremierRetouching)) ??
          null
        )
      }
    },
    /**
     * Assemble CartUpsellCardContent[] for a session type by filtering the
     * API add-ons array to those with a matching cart upsell content registry
     * entry. Pricing comes from the API; display content from the registry.
     *
     * @param key - Session type to build upsell cards for.
     * @returns Cards for add-ons with a matching cart upsell content entry.
     *          Empty array when no upsell add-ons are available.
     */
    getCartUpsellCards(): (key: SessionTypeKey) => CartUpsellEnriched[] {
      return (key) => {
        const sessionType = this.catalogWithContent[key]
        if (!sessionType) return []
        const contentStore = useContentStore()
        return sessionType.cartUpsells
          .filter((upsell) => !!cartUpsellFallbacks[upsell.catalogBundle.bundle?.identifier ?? ''])
          .map((upsell) => {
            const id = upsell.catalogBundle.bundle?.identifier ?? ''
            const content = contentStore.resolve<CheckoutCartUpsell>(
              ContentType.CHECKOUT_CART_UPSELL,
              id,
              cartUpsellFallbacks[id]
            )
            return { ...upsell, content }
          })
      }
    },
    /**
     * Cart upsell cards for the currently selected session type.
     *
     * When the customer has both a prepay package and Premier Retouching,
     * Premier supersedes the Artist Touch upsell (SUN-251 item 3.1): Artist
     * Touch is filtered out, which promotes B&W Proofs to the primary slot.
     * The `autoUpsertCartUpsells` guard on the cart keeps the actual cart
     * bundle in sync; this getter only governs what the customer sees.
     */
    cartUpsellCards(): CartUpsellEnriched[] {
      const selections = useBookingSelectionsStore()
      const key = selections.selectedSessionTypeKey
      if (!key) return []
      const cards = this.getCartUpsellCards(key)
      if (!selections.hasPrepayAndPremierRetouching) return cards
      return cards.filter(
        (card) => !getBundleAddOnIds(card.catalogBundle).has(CadyAddOn.ArtistTouch)
      )
    },
    /**
     * True when the Basic tier still owes an explicit yes/no on a visible cart
     * upsell. Basic upsells are never auto-selected, so the customer must
     * decide each one (add to cart or decline) before leaving Review &
     * Checkout — this gates the account-form Continue and drives the
     * required-selection message. Restores the enforcement dropped in the
     * 2026-04 cart-upsell rework (`allUpsellsDecided`).
     *
     * A card counts as decided when it is in the cart (added) or in
     * `declinedCartUpsellIds` (declined). Reads the already-filtered
     * `cartUpsellCards`, so an Artist Touch hidden by Premier Retouching
     * (SUN-251) is not treated as undecided. Non-Basic tiers auto-select their
     * upsells and are never pending.
     */
    basicCartUpsellSelectionPending(): boolean {
      const selections = useBookingSelectionsStore()
      if (!selections.isBasicTier) return false
      const cartStore = useCartStore()
      return this.cartUpsellCards.some((card) => {
        const id = card.catalogBundle.catalogBundleId
        if (!id) return false
        return (
          !cartStore.catalogBundleIsInCart(id) && !selections.declinedCartUpsellIds.includes(id)
        )
      })
    },
    /**
     * Flat `catalogBundleId → CMS-friendly label` map across all loaded
     * session types. Only `content.orderSummaryFriendlyName` is consulted —
     * `heading`/`title` are intentionally excluded since they target other
     * surfaces (cards, hero copy). When the friendly name is empty the entry
     * is omitted, and consumers should reach for `orderSummaryLabels` instead
     * of pairing this with their own `|| bundleNameInternal` fallback.
     */
    orderSummaryLabelIndex(): Record<string, string> {
      const contentStore = useContentStore()
      const labels: Record<string, string> = {}
      const set = (id: string | undefined | null, friendlyName: string | undefined | null) => {
        if (id && friendlyName) labels[id] = friendlyName
      }
      for (const [key, sessionType] of Object.entries(this.catalogWithContent)) {
        if (!sessionType) continue
        const primary = sessionType.primarySessionBundle
        set(primary.catalogBundleId, primary.content.orderSummaryFriendlyName)
        for (const addOn of sessionType.addOns) {
          set(addOn.catalogBundleId, addOn.content?.orderSummaryFriendlyName)
        }
        for (const prepay of sessionType.prePays) {
          set(prepay.catalogBundleId, prepay.content?.orderSummaryFriendlyName)
        }
        for (const upsell of this.getCartUpsellCards(key as SessionTypeKey)) {
          set(upsell.catalogBundle.catalogBundleId, upsell.content?.orderSummaryFriendlyName)
        }
        const premierRetouching = sessionType.prePays.find((p) =>
          getBundleAddOnIds(p).has(CadyAddOn.PremierRetouching)
        )
        if (premierRetouching) {
          const premierRetouchingContent = contentStore.resolve<CheckoutPremierRetouching>(
            ContentType.CHECKOUT_PREMIER_RETOUCHING,
            'default',
            checkoutPremierRetouchingFallback
          )
          set(premierRetouching.catalogBundleId, premierRetouchingContent.orderSummaryFriendlyName)
        }
      }
      return labels
    },
    /**
     * Pre-confirmation order-summary labels resolved per cart bundle.
     * Combines `orderSummaryLabelIndex` (CMS friendly names) with the
     * `cb.bundleNameInternal` fallback so consumers can `labels[catalogBundleId]`
     * and get a guaranteed-non-empty display string in one step. This is the
     * canonical API for pre-confirmation surfaces; reach for `orderSummaryLabelIndex`
     * directly only when you don't have a cart in hand.
     */
    orderSummaryLabels(): Record<string, string> {
      const cartStore = useCartStore()
      const index = this.orderSummaryLabelIndex
      const labels: Record<string, string> = {}
      for (const cb of cartStore.cartBundles) {
        labels[cb.catalogBundleId] = index[cb.catalogBundleId] || cb.bundleNameInternal
      }
      return labels
    },
    /** Sum of prices for all "added" add-ons for the currently selected session type. */
    addOnsTotalValue(): number {
      const selections = useBookingSelectionsStore()
      const key = selections.selectedSessionTypeKey
      const addOnSelections = selections.addOnSelections
      const sections = key ? (this.getAddOnsPageContent(key)?.sections ?? []) : []
      return sections.reduce((sum, section) => {
        if (addOnSelections[section.identifier] === AddOnSelectionState.Added) {
          return sum + section.addButtonPriceValue
        }
        return sum
      }, 0)
    },
    /** Total price contribution from cart upsell bundles that are currently in the cart. */
    upsellTotal(): number {
      const cartStore = useCartStore()
      return this.cartUpsellCards.reduce((sum, card) => {
        if (
          card.catalogBundle.catalogBundleId &&
          cartStore.catalogBundleIsInCart(card.catalogBundle.catalogBundleId)
        ) {
          return sum + (card.catalogBundle.pricing?.basePrice ?? 0)
        }
        return sum
      }, 0)
    },
    /**
     * Combined order total: session base price + add-ons + in-cart upsells.
     * Does not include tax, digital processing fee, or discounts — those are
     * display-layer concerns applied in the checkout views.
     */
    orderTotal(): number {
      const basePrice = useBookingSelectionsStore().selectedBundlePrice
      return basePrice + this.addOnsTotalValue + this.upsellTotal
    },
    /**
     * Item options for the HAIR_OR_MAKEUP add-on bundle for the currently selected
     * session type. Returns the items from the first item group.
     * Empty array if the bundle is not present.
     */
    hairOrMakeupItemOptions(): ItemStorefrontDto[] {
      const key = useBookingSelectionsStore().selectedSessionTypeKey
      if (!key) return []
      const sessionType = this.catalogWithContent[key]
      if (!sessionType) return []
      const bundle = sessionType.addOns.find((a) => a.bundle?.identifier === 'HAIR_OR_MAKEUP')
      if (!bundle) return []
      const itemGroup = bundle.bundle?.itemGroups?.[0]
      if (!itemGroup?.itemGroupItems) return []
      return itemGroup.itemGroupItems
        .filter((igi): igi is typeof igi & { item: ItemStorefrontDto } => igi.item != null)
        .map((igi) => igi.item)
    },
    /** Whether all add-on sections for the currently selected session type have been decided. */
    allAddOnsDecided(): boolean {
      const selections = useBookingSelectionsStore()
      const key = selections.selectedSessionTypeKey
      const addOnSelections = selections.addOnSelections
      const sections = key ? (this.getAddOnsPageContent(key)?.sections ?? []) : []
      return sections.every(
        (section) =>
          addOnSelections[section.identifier] !== undefined &&
          addOnSelections[section.identifier] !== AddOnSelectionState.None
      )
    },
    /** Add-ons page content for the currently selected session type. */
    selectedAddOnsPageContent(): AddOnsPageContent | null {
      const key = useBookingSelectionsStore().selectedSessionTypeKey
      return key ? this.getAddOnsPageContent(key) : null
    },
    /** Pre-pay cards for the currently selected session type. */
    selectedPrepayCards(): PrepayCardEnriched[] {
      const key = useBookingSelectionsStore().selectedSessionTypeKey
      return key ? this.getPrepayCards(key) : []
    },
    /** Premier retouching bundle for the currently selected session type. */
    selectedPremierRetouchingBundle(): PrepayBundleWithContent | null {
      const key = useBookingSelectionsStore().selectedSessionTypeKey
      return key ? this.getPremierRetouchingBundle(key) : null
    },
    /**
     * Add-on sections that the user has accepted ("added") for the currently
     * selected session type. Used to build extras line items in checkout views.
     */
    selectedAddOnsEnriched(): AddOnSectionEnriched[] {
      const selections = useBookingSelectionsStore()
      const key = selections.selectedSessionTypeKey
      const sections = key ? (this.getAddOnsPageContent(key)?.sections ?? []) : []
      return sections.filter(
        (section) => selections.addOnSelections[section.identifier] === AddOnSelectionState.Added
      )
    },
    /**
     * Synthesizes the client-side Hair and Makeup upgrade-trigger sections
     * for Elite-tier Add-Ons pages, per ADR 011. Hair and Makeup on Elite
     * are not purchasable add-ons — they are upgrade triggers to Elite Plus
     * (where the chosen item is included via the EP toggle). Each section
     * carries `kind: AddOnSectionKind.UPGRADE_TRIGGER` and a CTA price showing the
     * Elite → EP base-price delta.
     *
     * Backend may still ship HAIR / MAKEUP add-on bundles in Elite catalog
     * responses during the cleanup transition; `getAddOnsPageContent`
     * filters them out before calling this. The synthesized sections render
     * regardless of catalog state.
     */
    synthesizeEliteHairMakeupTriggers(): AddOnSectionEnriched[] {
      const contentStore = useContentStore()
      const elitePrice = this.basePriceByKey[SessionType.Elite] ?? 0
      const epPrice = this.basePriceByKey[SessionType.ElitePlus] ?? 0
      const delta = epPrice - elitePrice
      const sections: AddOnSectionEnriched[] = []
      for (const id of Object.keys(ELITE_TRIGGER_ID_TO_CADY_ADDON)) {
        const fallback = addOnContentFallbacks[id]
        if (!fallback) continue
        const content = contentStore.resolve<BookingAddOnContent>(
          ContentType.ADD_ON_CONTENT,
          id,
          fallback
        )
        sections.push({
          ...content,
          addButtonPriceValue: delta,
          addButtonPrice: formatPriceDelta(delta),
          kind: AddOnSectionKind.UPGRADE_TRIGGER
        })
      }
      return sections
    },
    /**
     * Aggregated `CadyAddOn` ids for add-on bundles currently in the Added
     * selection state for the selected session type. Used by
     * `bookingSelectionsStore.effectiveCadySessionTypeId` to escalate the
     * availability/scheduling session-type ID when the cart implies a
     * silent backend tier upgrade per the SY27 merchandising spec.
     *
     * The EP `HAIR_OR_MAKEUP` toggle is a "pick one" bundle: its itemGroup
     * contains both Hair and Makeup items but only one is actually
     * attached. We filter to the user's selected item via
     * `bookingSelections.hairOrMakeupItemIdentifier`. All other bundles
     * attach their full item set, so no filter is applied.
     */
    selectedAddedCadyAddOnIds(): Set<CadyAddOn> {
      const selections = useBookingSelectionsStore()
      const key = selections.selectedSessionTypeKey
      if (!key) return new Set()
      const addOns = this.catalogWithContent[key]?.addOns ?? []
      const aggregated = new Set<CadyAddOn>()
      for (const addOn of addOns) {
        const sectionId = addOn.bundle?.identifier
        if (!sectionId) continue
        if (selections.addOnSelections[sectionId] !== AddOnSelectionState.Added) continue
        if (sectionId === 'HAIR_OR_MAKEUP') {
          const picked = selections.hairOrMakeupItemIdentifier
          if (!picked) continue
          for (const id of getBundleAddOnIds(addOn, picked)) aggregated.add(id)
        } else {
          for (const id of getBundleAddOnIds(addOn)) aggregated.add(id)
        }
      }
      return aggregated
    },
    cartSectionsForSummary(): CartSections {
      return this.buildCartSections({ scenesPriceLabel: '', includeUpsellsInAdditional: false })
    },
    cartSectionsForPay(): CartSections {
      return this.buildCartSections({
        scenesPriceLabel: 'Included',
        includeUpsellsInAdditional: true
      })
    },
    /**
     * Scene catalog with the Yearbook entry's image swapped for the school's
     * override URL when provided. Read this rather than the raw catalog so the
     * override applies uniformly on the scene selection page and the cart
     * summary/pay views.
     */
    schoolAdjustedSceneCatalog(): SceneCatalogEntry[] {
      const contentStore = useContentStore()
      const entries = contentStore.resolveEntries<SceneCatalogEntry>(
        ContentType.SCENE_CATALOG_ENTRY
      )
      const base = entries.length > 0 ? entries : sceneCatalogFallback
      const overrideUrl = this.schoolData?.yearbookSceneOverridePhotoUrl
      if (!overrideUrl) return base
      const yearbookSceneId = localSceneId(CadyScene.Yearbook)
      return base.map((entry) =>
        entry.identifier === yearbookSceneId
          ? { ...entry, image: { ...entry.image, url: overrideUrl } }
          : entry
      )
    },
    buildCartSections(): (opts: {
      scenesPriceLabel: string
      includeUpsellsInAdditional: boolean
    }) => CartSections {
      return ({ scenesPriceLabel, includeUpsellsInAdditional }) => {
        const cartStore = useCartStore()
        const selections = useBookingSelectionsStore()
        const key = selections.selectedSessionTypeKey
        const sessionType = key ? this.catalogWithContent[key] : undefined
        const additionalIds = new Set<string>()
        sessionType?.addOns.forEach(
          (b) => b.catalogBundleId && additionalIds.add(b.catalogBundleId)
        )
        sessionType?.prePays.forEach(
          (b) => b.catalogBundleId && additionalIds.add(b.catalogBundleId)
        )
        this.catalogData?.surchargeBundles?.forEach(
          (b) => b.catalogBundleId && additionalIds.add(b.catalogBundleId)
        )
        if (includeUpsellsInAdditional) {
          sessionType?.cartUpsells.forEach((u) => {
            const id = u.catalogBundle.catalogBundleId
            if (id) additionalIds.add(id)
          })
        }
        const sessionCatalogBundleId = sessionType?.primarySessionBundle.catalogBundleId
        const sessionCartBundle = sessionCatalogBundleId
          ? cartStore.cartBundles.find((cb) => cb.catalogBundleId === sessionCatalogBundleId)
          : undefined
        const studio = selections.scheduledLocation
        const sections: CartSections = {
          schedule: {
            studioName: studio?.LocationName ?? '',
            studioAddress: studio ? formatLocationFullAddress(studio) : '',
            date: selections.scheduledSessionDate
              ? dayjs(selections.scheduledSessionDate).format('dddd, MMMM D, YYYY')
              : '',
            time: selections.scheduledSessionTime ?? '',
            duration: ''
          }
        }
        if (selections.scheduledPresentationDate) {
          sections.presentation = {
            studioName: studio?.LocationName ?? '',
            studioAddress: studio ? formatLocationFullAddress(studio) : '',
            date: dayjs(selections.scheduledPresentationDate).format('dddd, MMMM D, YYYY'),
            time: selections.scheduledPresentationTime ?? '',
            duration: ''
          }
        }
        if (sessionCartBundle && sessionType) {
          const sceneCatalog = this.schoolAdjustedSceneCatalog
          sections.session = {
            bundleTitle:
              sessionType.primarySessionBundle.content.orderSummaryFriendlyName ||
              sessionType.primarySessionBundle.content.title,
            formattedPrice: formatCurrencyPrecise(sessionCartBundle.bundleTotalPostDiscounts),
            features: buildSessionSectionFeatures(sessionType.primarySessionBundle.content)
          }
          sections.scenes = {
            scenes: resolveCartBundleScenes(
              sessionCartBundle,
              sessionType.primarySessionBundle.sceneGroups,
              sceneCatalog
            ),
            priceLabel: scenesPriceLabel
          }
        }
        const additionalCartBundles = cartStore.cartBundles.filter((cb) =>
          additionalIds.has(cb.catalogBundleId)
        )
        if (additionalCartBundles.length > 0) {
          const labels = this.orderSummaryLabels
          sections.additionalItems = {
            items: additionalCartBundles.map((cb) => ({
              title: labels[cb.catalogBundleId],
              formattedPrice: formatCurrencyPrecise(cb.bundleTotalPostDiscounts)
            }))
          }
        }
        return sections
      }
    },
    cartTotalFormatted(): string {
      const cartStore = useCartStore()
      const total =
        cartStore.cartData?.order?.orderTotals?.total ??
        cartStore.cartData?.subtotalPostDiscounts ??
        0
      return formatCurrencyPrecise(total)
    },
    /**
     * Whether the persisted `scheduledLocation` is still compatible with
     * the current session-type and scene selections. Defense-in-depth
     * guard for `ScheduleSessionView.created()` — session-type and scene
     * mutation actions already clear scheduling at the source per ADR
     * 2026.04.28-001, so this should only return false if a future
     * mutation path forgets to invalidate.
     *
     * Each branch validates scenes against a different source:
     * - **School mode:** the front-end roadshow allowlist via
     *   `isRoadshowCompatibleScene` — per-location `AvailableScenes`
     *   from CADY Core is unreliable for Roadshow locations.
     * - **Studio mode:** `stored.AvailableScenes` on the persisted DTO,
     *   which is reliable for Studio locations and available before the
     *   live locations list has loaded.
     */
    storedScheduleIsCompatible(): boolean {
      const bookingSelectionsStore = useBookingSelectionsStore()
      const cartStore = useCartStore()
      const stored = bookingSelectionsStore.scheduledLocation
      if (!stored) return true
      const isStudio =
        bookingSelectionsStore.selectedLocationType === SchedulingLocationToggleType.Studio
      if (!isStudio) {
        if (stored.LocationTypeId !== LocationTypeId.Roadshow) return false
        return bookingSelectionsStore.selectedSceneIds.every((id) => isRoadshowCompatibleScene(id))
      }
      if (stored.LocationTypeId !== LocationTypeId.Studio) return false
      const sessionTypeId = bookingSelectionsStore.effectiveCadySessionTypeId
      if (sessionTypeId && !stored.AvailableSessionTypes.includes(sessionTypeId)) return false
      const sceneEnumValues = cartStore.cadySceneEnumValuesInCart
      return sceneEnumValues.every((scene) => stored.AvailableScenes.includes(scene))
    }
  },
  actions: {
    /**
     * Fetch full school detail by school ID and store it.
     * Called after the user selects a school in the FindSchoolModal.
     */
    async loadSchoolDetail(schoolId: number): Promise<{ success: boolean; message: string }> {
      try {
        const service = new Sunny16CommerceCadyService()
        this.selectedSchoolDetail = await service.getSchoolDetails(schoolId)
        return { success: true, message: '' }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to load school details'
        console.error('[bookingCommerceStore] loadSchoolDetail failed', error)
        return { success: false, message }
      }
    },
    /**
     * Load the booking catalog for the given catalog path.
     * The path is derived from `mainStore.catalogPath` and includes
     * the environment prefix, active year, state, and school ID.
     */
    async loadCatalog(catalogPath: string): Promise<{ success: boolean; message: string }> {
      this.catalogLoading = true
      this.catalogError = null
      const sunny16CommerceStorefrontService = new Sunny16CommerceBookingCatalogService()
      try {
        this.catalogData = await sunny16CommerceStorefrontService.getBookingCatalog(catalogPath)
        return { success: true, message: '' }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to load catalog'
        console.error('[bookingCommerceStore] loadCatalog failed', error)
        this.catalogError = message
        return { success: false, message }
      } finally {
        this.catalogLoading = false
      }
    },
    /**
     * Look up a single session type by its key.
     * Returns null if the catalog is not loaded or the key has no data.
     */
    getSessionType(key: SessionTypeKey): BookingSessionTypeDto | null {
      return this.sessionTypes?.[key] ?? null
    },
    /**
     * Load the catalog and then fetch CMS content for the catalog.
     * Combines `loadCatalog` + `contentStore.loadCatalogContent` into a
     * single entry point so callers don't need to orchestrate both.
     *
     * Cross-store note: this action and `resetStore` below both write to
     * `mainStore.dataLoadedAt`. The 12h data-expiration policy
     * (`lib/dataExpiration.ts`) reads that field, but it lives on
     * `mainStore` rather than this store because today the catalog and
     * content reload functionality is bound together via this single
     * entry point — one shared timestamp matches the load surface. If the
     * load entry points ever split (per-catalog vs. per-content), the
     * timestamp can split with them.
     */
    async loadCatalogWithContent(
      catalogPath: string
    ): Promise<{ success: boolean; message: string }> {
      const catalogResult = await this.loadCatalog(catalogPath)
      if (!catalogResult.success || !this.catalogData?.catalogId) {
        return catalogResult
      }
      // `contentStore.loadCatalogContent` swallows network errors and
      // returns `{ success: false }` rather than throwing — propagate
      // that failure as this function's result so callers (the toast
      // handler in `expireAndReset`, `SessionsListingPageView`, etc.)
      // see the underlying content-load failure.
      const contentStore = useContentStore()
      const contentResult = await contentStore.loadCatalogContent(this.catalogData.catalogId)
      if (!contentResult.success) {
        return contentResult
      }
      await this.loadPromotionDealCards()
      // Stamp the data-expiration timestamp only after both catalog and
      // content have finished loading successfully. Any failure leaves
      // the prior timestamp untouched so a transient error doesn't reset
      // the TTL window or mark stale state as fresh.
      useMainStore().setDataLoadedAt(Date.now())
      return catalogResult
    },
    async loadPromotionDealCards(): Promise<void> {
      const contentStore = useContentStore()
      await contentStore.fetchBatch(sessionDealBatchItems)
    },
    /** Clear all catalog data and reset the store to its initial state. */
    resetStore() {
      this.selectedSchoolDetail = null
      this.catalogData = null
      this.catalogLoading = false
      this.catalogError = null
      // Clear the data-expiration timestamp so a manual reset behaves
      // identically to the automatic expire-and-reset flow.
      useMainStore().setDataLoadedAt(null)
    }
  },
  persist: true
})
